import { X } from 'lucide-react-native';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { Input } from '@/src/components/Input';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export function PhoneVerificationCodeModal({
  code,
  description = 'Enter the SMS code we sent to your phone number.',
  loading,
  onChangeCode,
  onClose,
  onConfirm,
  title = 'Enter verification code',
  visible,
}: {
  code: string;
  description?: string;
  loading?: boolean;
  onChangeCode: (code: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.root}
      >
        <Pressable
          accessibilityLabel="Close phone verification code"
          style={styles.backdrop}
          onPress={onClose}
        />
        <View style={styles.card}>
          <View style={styles.header}>
            <View style={styles.titleGroup}>
              <Text style={styles.eyebrow}>Phone verification</Text>
              <Text style={styles.title}>{title}</Text>
            </View>
            <Pressable
              accessibilityLabel="Close phone verification code"
              hitSlop={10}
              style={styles.closeButton}
              onPress={onClose}
            >
              <X color={colors.text} size={22} strokeWidth={2.5} />
            </Pressable>
          </View>
          <Text style={styles.description}>{description}</Text>
          <Input
            autoComplete="sms-otp"
            editable={!loading}
            keyboardType="number-pad"
            label="SMS code"
            onChangeText={onChangeCode}
            placeholder="123456"
            textContentType="oneTimeCode"
            value={code}
          />
          <View style={styles.actions}>
            <Button
              disabled={loading}
              title="Cancel"
              variant="secondary"
              onPress={onClose}
            />
            <Button
              disabled={loading || !code.trim()}
              loading={loading}
              title="Verify phone"
              onPress={onConfirm}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(26, 12, 62, 0.52)',
  },
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    elevation: 8,
    gap: spacing.md,
    maxWidth: 460,
    padding: spacing.lg,
    shadowColor: '#000',
    shadowOffset: { height: 14, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    width: '100%',
  },
  closeButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  description: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  eyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  header: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  root: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.md,
  },
  title: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  titleGroup: {
    flex: 1,
    gap: spacing.xs,
  },
});
